
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type Gender = 'male' | 'female';
type Method = 'navy' | 'bmi' | 'skinfold';

const BodyFatCalculator = () => {
  // Basic measurements
  const [weight, setWeight] = useState<string>('70');
  const [height, setHeight] = useState<string>('170');
  const [gender, setGender] = useState<Gender>('male');
  const [method, setMethod] = useState<Method>('navy');
  
  // Navy method measurements
  const [waist, setWaist] = useState<string>('80');
  const [neck, setNeck] = useState<string>('36');
  const [hip, setHip] = useState<string>('90'); // Only used for women
  
  // Skinfold measurements
  const [chest, setChest] = useState<string>('');
  const [abdominal, setAbdominal] = useState<string>('');
  const [thigh, setThigh] = useState<string>('');
  const [triceps, setTriceps] = useState<string>('');
  const [subscapular, setSubscapular] = useState<string>('');
  const [suprailiac, setSuprailiac] = useState<string>('');
  
  const [bodyFat, setBodyFat] = useState<number | null>(null);
  const [fatMass, setFatMass] = useState<number | null>(null);
  const [leanMass, setLeanMass] = useState<number | null>(null);
  const [category, setCategory] = useState<string>('');

  const calculateNavyMethod = () => {
    const waistVal = parseFloat(waist);
    const neckVal = parseFloat(neck);
    const heightVal = parseFloat(height);
    
    let bodyFatPercentage: number;
    
    if (gender === 'male') {
      bodyFatPercentage = 495 / (1.0324 - 0.19077 * Math.log10(waistVal - neckVal) + 0.15456 * Math.log10(heightVal)) - 450;
    } else {
      const hipVal = parseFloat(hip);
      bodyFatPercentage = 495 / (1.29579 - 0.35004 * Math.log10(waistVal + hipVal - neckVal) + 0.22100 * Math.log10(heightVal)) - 450;
    }
    
    return Math.max(Math.min(bodyFatPercentage, 60), 2); // Clamp between 2% and 60%
  };
  
  const calculateBMIMethod = () => {
    const weightVal = parseFloat(weight);
    const heightVal = parseFloat(height) / 100; // Convert cm to meters
    
    const bmi = weightVal / (heightVal * heightVal);
    
    // Estimating body fat from BMI (this is a rough estimate)
    let bodyFatPercentage: number;
    
    if (gender === 'male') {
      bodyFatPercentage = 1.20 * bmi + 0.23 * parseInt(age) - 16.2;
    } else {
      bodyFatPercentage = 1.20 * bmi + 0.23 * parseInt(age) - 5.4;
    }
    
    return Math.max(Math.min(bodyFatPercentage, 60), 2); // Clamp between 2% and 60%
  };
  
  const calculateSkinfoldMethod = () => {
    // This is a simplified version using the Jackson-Pollock 3-site formula
    let sum = 0;
    
    if (gender === 'male') {
      sum = parseFloat(chest) + parseFloat(abdominal) + parseFloat(thigh);
      const bodyDensity = 1.10938 - 0.0008267 * sum + 0.0000016 * sum * sum - 0.0002574 * parseInt(age);
      return (4.95 / bodyDensity - 4.5) * 100;
    } else {
      sum = parseFloat(triceps) + parseFloat(suprailiac) + parseFloat(thigh);
      const bodyDensity = 1.099421 - 0.0009929 * sum + 0.0000023 * sum * sum - 0.0001392 * parseInt(age);
      return (4.95 / bodyDensity - 4.5) * 100;
    }
  };
  
  const [age, setAge] = useState<string>('30');

  const handleCalculate = () => {
    try {
      if (method === 'navy' && (!waist || !neck || (gender === 'female' && !hip))) {
        toast.error("Please fill in all required measurements");
        return;
      }
      
      if (method === 'bmi' && (!weight || !height || !age)) {
        toast.error("Please fill in weight, height, and age");
        return;
      }
      
      if (method === 'skinfold') {
        if (gender === 'male' && (!chest || !abdominal || !thigh)) {
          toast.error("Please fill in all skinfold measurements");
          return;
        }
        if (gender === 'female' && (!triceps || !suprailiac || !thigh)) {
          toast.error("Please fill in all skinfold measurements");
          return;
        }
      }
      
      let bodyFatPercentage: number;
      
      switch (method) {
        case 'navy':
          bodyFatPercentage = calculateNavyMethod();
          break;
        case 'bmi':
          bodyFatPercentage = calculateBMIMethod();
          break;
        case 'skinfold':
          bodyFatPercentage = calculateSkinfoldMethod();
          break;
        default:
          bodyFatPercentage = 0;
      }
      
      // Round to 1 decimal place
      bodyFatPercentage = Math.round(bodyFatPercentage * 10) / 10;
      
      const weightVal = parseFloat(weight);
      const fatMassVal = (bodyFatPercentage / 100) * weightVal;
      const leanMassVal = weightVal - fatMassVal;
      
      setBodyFat(bodyFatPercentage);
      setFatMass(Math.round(fatMassVal * 10) / 10);
      setLeanMass(Math.round(leanMassVal * 10) / 10);
      
      // Determine category
      setCategory(getBodyFatCategory(bodyFatPercentage, gender));
      
      toast.success("Body fat calculated successfully");
    } catch (error) {
      toast.error("Error calculating body fat percentage");
      console.error(error);
    }
  };
  
  const getBodyFatCategory = (bf: number, gender: Gender): string => {
    if (gender === 'male') {
      if (bf < 6) return "Essential Fat";
      if (bf < 14) return "Athletic";
      if (bf < 18) return "Fitness";
      if (bf < 25) return "Average";
      return "Obese";
    } else {
      if (bf < 14) return "Essential Fat";
      if (bf < 21) return "Athletic";
      if (bf < 25) return "Fitness";
      if (bf < 32) return "Average";
      return "Obese";
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Body Fat Calculator</CardTitle>
          <CardDescription>
            Calculate your body fat percentage using different methods.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="navy" onValueChange={(value) => setMethod(value as Method)}>
            <TabsList className="grid grid-cols-3 mb-6">
              <TabsTrigger value="navy">Navy Method</TabsTrigger>
              <TabsTrigger value="bmi">BMI Method</TabsTrigger>
              <TabsTrigger value="skinfold">Skinfold Method</TabsTrigger>
            </TabsList>
            
            <div className="mb-6">
              <Label>Gender</Label>
              <RadioGroup
                value={gender}
                onValueChange={(value: string) => setGender(value as Gender)}
                className="flex gap-4 mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="male" id="bf-male" />
                  <Label htmlFor="bf-male">Male</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="female" id="bf-female" />
                  <Label htmlFor="bf-female">Female</Label>
                </div>
              </RadioGroup>
            </div>
            
            <TabsContent value="navy" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="height">Height (cm)</Label>
                  <Input
                    id="height"
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="neck">Neck (cm)</Label>
                  <Input
                    id="neck"
                    type="number"
                    value={neck}
                    onChange={(e) => setNeck(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="waist">Waist (cm)</Label>
                  <Input
                    id="waist"
                    type="number"
                    value={waist}
                    onChange={(e) => setWaist(e.target.value)}
                    className="mt-1"
                  />
                </div>
                {gender === 'female' && (
                  <div>
                    <Label htmlFor="hip">Hip (cm)</Label>
                    <Input
                      id="hip"
                      type="number"
                      value={hip}
                      onChange={(e) => setHip(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="bmi" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="height-bmi">Height (cm)</Label>
                  <Input
                    id="height-bmi"
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="weight-bmi">Weight (kg)</Label>
                  <Input
                    id="weight-bmi"
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="age-bmi">Age (years)</Label>
                  <Input
                    id="age-bmi"
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Note: The BMI method provides only a rough estimate and may not be accurate for athletic individuals.
              </p>
            </TabsContent>
            
            <TabsContent value="skinfold" className="space-y-6">
              <p className="text-sm text-muted-foreground mb-4">
                Enter skinfold measurements in millimeters using calipers.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="age-skinfold">Age (years)</Label>
                  <Input
                    id="age-skinfold"
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                {gender === 'male' ? (
                  <>
                    <div>
                      <Label htmlFor="chest">Chest (mm)</Label>
                      <Input
                        id="chest"
                        type="number"
                        value={chest}
                        onChange={(e) => setChest(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="abdominal">Abdominal (mm)</Label>
                      <Input
                        id="abdominal"
                        type="number"
                        value={abdominal}
                        onChange={(e) => setAbdominal(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label htmlFor="triceps">Triceps (mm)</Label>
                      <Input
                        id="triceps"
                        type="number"
                        value={triceps}
                        onChange={(e) => setTriceps(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="suprailiac">Suprailiac (mm)</Label>
                      <Input
                        id="suprailiac"
                        type="number"
                        value={suprailiac}
                        onChange={(e) => setSuprailiac(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </>
                )}
                
                <div>
                  <Label htmlFor="thigh">Thigh (mm)</Label>
                  <Input
                    id="thigh"
                    type="number"
                    value={thigh}
                    onChange={(e) => setThigh(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="weight-skinfold">Weight (kg)</Label>
                  <Input
                    id="weight-skinfold"
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </TabsContent>
            
            <Button 
              onClick={handleCalculate} 
              className="w-full mt-6"
            >
              Calculate Body Fat
            </Button>
          </Tabs>

          {bodyFat !== null && (
            <div className="mt-8 p-6 border rounded-lg bg-blue-50">
              <h3 className="text-xl font-semibold mb-2">Your Results</h3>
              <div className="space-y-2">
                <p className="text-lg">
                  Body Fat: <span className="font-bold text-primary">{bodyFat}%</span>
                </p>
                <p>
                  Category: <span className="font-semibold">{category}</span>
                </p>
                <p>
                  Fat Mass: <span className="font-semibold">{fatMass} kg</span>
                </p>
                <p>
                  Lean Body Mass: <span className="font-semibold">{leanMass} kg</span>
                </p>
              </div>
              
              <p className="mt-4 text-sm text-muted-foreground">
                Note: This calculation is an estimate. For the most accurate measurements, consult with a healthcare professional.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BodyFatCalculator;
